import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useToast } from '../../hooks/use-toast';

const Register = ({ onLogin }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [clientFormData, setClientFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company: '',
    phone: ''
  });
  const [engineerFormData, setEngineerFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });

  const handleClientRegister = async (e) => {
    e.preventDefault();
    
    if (clientFormData.password !== clientFormData.confirmPassword) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas",
        variant: "destructive"
      });
      return;
    }

    try {
      const { authAPI } = await import('../../api');
      const data = await authAPI.register({
        email: clientFormData.email,
        password: clientFormData.password,
        name: clientFormData.name,
        company: clientFormData.company,
        phone: clientFormData.phone,
        role: 'client'
      });
      
      // Redirect to email verification page
      if (data.requires_verification) {
        toast({
          title: "Inscription réussie !",
          description: "Vérifiez votre email pour activer votre compte",
        });
        navigate('/verify-email', { 
          state: { 
            email: clientFormData.email,
            role: 'client'
          } 
        });
      } else {
        // Fallback si pas de vérification requise
        if (data.token) {
          localStorage.setItem('token', data.token);
          onLogin('client', data.user);
          navigate('/client/dashboard');
        }
      }
    } catch (error) {
      toast({
        title: "Erreur d'inscription",
        description: error.response?.data?.detail || "Une erreur est survenue",
        variant: "destructive"
      });
    }
  };

  const handleEngineerRegister = async (e) => {
    e.preventDefault();
    
    if (engineerFormData.password !== engineerFormData.confirmPassword) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas",
        variant: "destructive"
      });
      return;
    }

    try {
      const { authAPI } = await import('../../api');
      const data = await authAPI.register({
        email: engineerFormData.email,
        password: engineerFormData.password,
        name: engineerFormData.name,
        phone: engineerFormData.phone,
        role: 'engineer'
      });
      
      // Redirect to email verification page
      if (data.requires_verification) {
        toast({
          title: "Inscription réussie !",
          description: "Vérifiez votre email pour activer votre compte",
        });
        navigate('/verify-email', { 
          state: { 
            email: engineerFormData.email,
            role: 'engineer'
          } 
        });
      } else {
        // Fallback si pas de vérification requise
        if (data.token) {
          localStorage.setItem('token', data.token);
          onLogin('engineer', data.user);
          navigate('/engineer/dashboard');
        }
      }
    } catch (error) {
      toast({
        title: "Erreur d'inscription",
        description: error.response?.data?.detail || "Une erreur est survenue",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] flex items-center justify-center px-6 py-32">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2">
            <span className="text-[#A6A6A6]">GT</span>
            <span className="text-[#C00000]"> ENGINEERING</span>
          </h1>
          <p className="text-[#A6A6A6]">Engineered Performance.</p>
        </div>

        <Tabs defaultValue="client" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-[#1a1a1a] mb-6">
            <TabsTrigger value="client" className="data-[state=active]:bg-[#C00000] data-[state=active]:text-white">
              Client
            </TabsTrigger>
            <TabsTrigger value="engineer" className="data-[state=active]:bg-[#C00000] data-[state=active]:text-white">
              Ingénieur
            </TabsTrigger>
          </TabsList>

          {/* Client Registration */}
          <TabsContent value="client">
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardHeader>
                <CardTitle className="text-white">Créer un compte Client</CardTitle>
                <CardDescription className="text-[#A6A6A6]">
                  Rejoignez GT Engineering dès maintenant
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleClientRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="client-name" className="text-[#A6A6A6]">Nom complet</Label>
                    <Input
                      id="client-name"
                      type="text"
                      placeholder="Jean Martin"
                      value={clientFormData.name}
                      onChange={(e) => setClientFormData({ ...clientFormData, name: e.target.value })}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client-email" className="text-[#A6A6A6]">Email</Label>
                    <Input
                      id="client-email"
                      type="email"
                      placeholder="votre@email.com"
                      value={clientFormData.email}
                      onChange={(e) => setClientFormData({ ...clientFormData, email: e.target.value })}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client-company" className="text-[#A6A6A6]">Entreprise</Label>
                    <Input
                      id="client-company"
                      type="text"
                      placeholder="Nom de votre entreprise"
                      value={clientFormData.company}
                      onChange={(e) => setClientFormData({ ...clientFormData, company: e.target.value })}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client-phone" className="text-[#A6A6A6]">Téléphone</Label>
                    <Input
                      id="client-phone"
                      type="tel"
                      placeholder="+33612345678"
                      value={clientFormData.phone}
                      onChange={(e) => setClientFormData({ ...clientFormData, phone: e.target.value })}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client-password" className="text-[#A6A6A6]">Mot de passe</Label>
                    <Input
                      id="client-password"
                      type="password"
                      placeholder="••••••••"
                      value={clientFormData.password}
                      onChange={(e) => setClientFormData({ ...clientFormData, password: e.target.value })}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client-confirmPassword" className="text-[#A6A6A6]">Confirmer le mot de passe</Label>
                    <Input
                      id="client-confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={clientFormData.confirmPassword}
                      onChange={(e) => setClientFormData({ ...clientFormData, confirmPassword: e.target.value })}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full bg-[#C00000] hover:bg-[#C00000]/90 text-white font-semibold">
                    S'inscrire comme Client
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Engineer Registration */}
          <TabsContent value="engineer">
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardHeader>
                <CardTitle className="text-white">Créer un compte Ingénieur</CardTitle>
                <CardDescription className="text-[#A6A6A6]">
                  Rejoignez l'équipe GT Engineering
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleEngineerRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="engineer-name" className="text-[#A6A6A6]">Nom complet</Label>
                    <Input
                      id="engineer-name"
                      type="text"
                      placeholder="Jean Martin"
                      value={engineerFormData.name}
                      onChange={(e) => setEngineerFormData({ ...engineerFormData, name: e.target.value })}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="engineer-email" className="text-[#A6A6A6]">Email professionnel</Label>
                    <Input
                      id="engineer-email"
                      type="email"
                      placeholder="ingenieur@gt-engineering.com"
                      value={engineerFormData.email}
                      onChange={(e) => setEngineerFormData({ ...engineerFormData, email: e.target.value })}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="engineer-phone" className="text-[#A6A6A6]">Téléphone</Label>
                    <Input
                      id="engineer-phone"
                      type="tel"
                      placeholder="+33612345678"
                      value={engineerFormData.phone}
                      onChange={(e) => setEngineerFormData({ ...engineerFormData, phone: e.target.value })}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      required
                    />
                    <p className="text-xs text-[#A6A6A6]">Pour recevoir les notifications SMS des nouvelles commandes</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="engineer-password" className="text-[#A6A6A6]">Mot de passe</Label>
                    <Input
                      id="engineer-password"
                      type="password"
                      placeholder="••••••••"
                      value={engineerFormData.password}
                      onChange={(e) => setEngineerFormData({ ...engineerFormData, password: e.target.value })}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="engineer-confirmPassword" className="text-[#A6A6A6]">Confirmer le mot de passe</Label>
                    <Input
                      id="engineer-confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={engineerFormData.confirmPassword}
                      onChange={(e) => setEngineerFormData({ ...engineerFormData, confirmPassword: e.target.value })}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full bg-[#C00000] hover:bg-[#C00000]/90 text-white font-semibold">
                    S'inscrire comme Ingénieur
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="text-center mt-6">
          <p className="text-[#A6A6A6] text-sm">
            Déjà un compte ?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-[#C00000] hover:underline font-semibold"
            >
              Se connecter
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
